import { useMutation, useQuery } from "@tanstack/react-query";
import { Col, Form, Input, Row, Select, Spin, message } from "antd";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  createProject,
  getProjectInfoById,
  updateProjectInfo,
} from "../../../../../../api/apiCallManager";
import FormLabel from "../../../../../../components/label/FormLabel";
import { LOGGER } from "../../../../../../helpers/logger";
import useRolesListStore from "../../../../../../zustand/store/roleListStore";
import useFLProjectCreationTab from "../../../../../../zustand/store/useFLProjectCreationTab";
import useProfileDataStore from "./../../../../../../zustand/store/profileDataStore";
import {
  GET_LOCAL_STORAGE,
  LOCAL_STORAGE_KEY,
  SET_LOCAL_STORAGE,
} from "../../../../../../helpers/localStorage";
import AccessTo from "../../../../../../components/is-show/AccessTo";
import { PA, PFA } from "../../../../../router/AddRoutesJson";
import {
  getCurrentRole,
  isClientManager,
  isCurrentRoleReadOnly,
  isFederatedLearning,
  isMachineLearning,
  isPlatformAdmin,
  isProjectAdmin,
} from "../../../../../../helpers/authFunctions";
import useProjectInfoQuery from "../../../../../../components/hooks/useProjectInfoQuery";
import { showError, showSuccess } from "../../../../../../helpers/alerts";
import { LoadingOutlined } from "@ant-design/icons";

const ProjectInfoForm = () => {
  const [form] = Form.useForm();
  const [localID, setLocalID] = useState("");
  const userProfile = useProfileDataStore((state) => state.userProfile);
  const { setActiveKey } = useFLProjectCreationTab();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    data: projectInfoData,
    error,
    isLoading: isProjectInfoDataLoading,
  } = useProjectInfoQuery(id, getProjectInfoById);
  const secondTab = () => {
    setActiveKey("TAB_CLIENT");
  };
  const createProjectInfoMutation = useMutation({
    mutationFn: createProject,
    onSuccess: (data) => {
      if (data.statusCode == 200) {
        showSuccess("Project Info Created Successfully!");
        setLocalID(data.body);
        SET_LOCAL_STORAGE(LOCAL_STORAGE_KEY.currentFLProjectId, data.body);
        navigate(`/fl-project`);
        // secondTab();
      } else {
        showError(data.message || data.body || "Something went wrong!");
      }
    },
  });
  const updateProjectInfoMutation = useMutation({
    mutationFn: updateProjectInfo,
    onSuccess: (data) => {
      if (data.statusCode == 200) {
        showSuccess("Project Info Update Successfully!");
        secondTab();
      }
    },
  });
  const onFinish = (values) => {
    LOGGER("Form values:", values);
    // let localID = GET_LOCAL_STORAGE(LOCAL_STORAGE_KEY.currentFLProjectId);
    const isUpdateId = localID || id || false;
    // const isUpdateId = id || false;
    if (isUpdateId) {
      const updatedData = {
        record_id: isUpdateId,
        projectType: location.pathname == "/create-ml-project" ? "ML" : "FL",
        ...values,
      };
      updateProjectInfoMutation.mutate(updatedData);
    } else {
      const newData = {
        projectType: location.pathname == "/create-ml-project" ? "ML" : "FL",
        ...values,
      };
      createProjectInfoMutation.mutate(newData);
    }
  };

  const onFinishFailed = (errorInfo) => {
    LOGGER("Failed:", errorInfo);
  };

  const { rolesList } = useRolesListStore();
  const onlyEnablePA = !isProjectAdmin();
  return (
    <div className={`p-5  ${onlyEnablePA && "disableAll"}`}>
      <Spin spinning={isProjectInfoDataLoading}>
        <Form
          key={projectInfoData + userProfile}
          name="projectForm"
          onFinish={onFinish}
          initialValues={{
            ...projectInfoData,
          }}
          requiredMark={false}
          onFinishFailed={onFinishFailed}
          layout="vertical"
          autoComplete="false"
          disabled={onlyEnablePA}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="projectTitle"
                label={<FormLabel>Project Title</FormLabel>}
                rules={[
                  { required: true, message: "Please enter the project title" },
                  {
                    pattern: /^[a-zA-Z0-9_\-\s]*$/,
                    message:
                      "Only letters, numbers, underscores, and hyphens are allowed",
                  },
                ]}
              >
                <Input spellCheck={true} />
              </Form.Item>
            </Col>
            {/* <Col span={12}>
            <Form.Item
              name="email"
              label={<FormLabel>Email</FormLabel>}
              rules={[
                {
                  required: true,
                  message: "Please enter the personal access token",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col> */}
            <Col span={12}>
              <Form.Item
                name="projectOwner"
                label={<FormLabel>Project Owner</FormLabel>}
                rules={[
                  { required: true, message: "Please enter the project owner" },
                ]}
              >
                <Select>
                  {rolesList?.allPA?.map((engineer) => (
                    <Select.Option key={engineer.email} value={engineer.email}>
                      {engineer.userName} ({engineer.email})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="federatedEngineer"
                label={<FormLabel>Federated Learning Engineer</FormLabel>}
                rules={[
                  {
                    required: true,
                    message: "Please select the federated learning engineer",
                  },
                ]}
              >
                <Select>
                  {rolesList?.allFL?.map((engineer) => (
                    <Select.Option key={engineer.email} value={engineer.email}>
                      {engineer.userName} ({engineer.email})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="jiraUrl"
                label={<FormLabel requiredMark={false}>Jira URL</FormLabel>}
                rules={[{ type: "url", message: "Please enter a valid URL" }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="mlEngineer"
                label={<FormLabel>Machine Learning Engineer</FormLabel>}
                rules={[
                  {
                    required: true,
                    message: "Please select the machine learning engineer",
                  },
                ]}
              >
                <Select>
                  {rolesList?.allML?.map((engineer) => (
                    <Select.Option key={engineer.id} value={engineer.email}>
                      {engineer.userName} ({engineer.email})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* <Col span={12}>
            <Form.Item
              name="githubUrl"
              label={<FormLabel>Github URL</FormLabel>}
              rules={[
                { required: true, message: "Please enter the Github URL" },
              ]}
            >
              <Input />
            </Form.Item>
          </Col> */}
            <Col span={12}>
              <Form.Item
                name="confluenceUrl"
                label={
                  <FormLabel requiredMark={false}>Confluence URL</FormLabel>
                }
                rules={[{ type: "url", message: "Please enter a valid URL" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="projectDescription"
                label={<FormLabel>Project Description</FormLabel>}
                rules={[
                  {
                    required: true,
                    message: "Please enter the project description",
                  },
                ]}
              >
                <Input.TextArea />
              </Form.Item>
            </Col>
          </Row>
          {/* {JSON.stringify(projectInfoData.projectStatus)} */}
          {projectInfoData?.projectStatus == "Ready to Train" ||
          projectInfoData?.projectStatus == "Completed" ? (
            ""
          ) : (
            <AccessTo role={[PA]}>
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={localID || createProjectInfoMutation.isPending}
                  className="bg-[#0057B8] py-2 px-4 rounded-sm text-[13px] w-[300px] text-white"
                >
                  {createProjectInfoMutation.isPending && (
                    <Spin
                      className="me-2"
                      indicator={
                        <LoadingOutlined spin style={{ color: "white" }} />
                      }
                      size="small"
                    />
                  )}
                  {projectInfoData ? "Update" : "Submit"}
                </button>
              </div>
            </AccessTo>
          )}
        </Form>
      </Spin>
    </div>
  );
};

export default ProjectInfoForm;
